import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { AUTOMATION_RUN_KEY, AUTOMATION_RUNS_KEY } from '@/utils/queryKeys.ts';
import { AxiosError } from 'axios';
import { get_run_url } from '@/utils/urls.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { notifications } from '@mantine/notifications';
import useRunId from '@/hooks/useRunId.ts';
import { useNavigate } from 'react-router-dom';
import { SimpleResponse } from '@/types/common.ts';
import useRetrieveAutomationRuns from '@/hooks/api/runs/useRetrieveAutomationRuns.ts';
import { useMemo } from 'react';
import { flattenInfiniteQueryData } from '@/utils/misc.ts';

const useDeleteRun = (runId: string): UseMutationResult<SimpleResponse, AxiosError> => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const automationId = useAutomationId();
  const currentRunId = useRunId();
  const navigate = useNavigate();

  const { data: runs } = useRetrieveAutomationRuns({});

  const allRuns = useMemo(() => flattenInfiniteQueryData(runs), [runs]);

  return useMutation({
    mutationFn: async () => {
      if (runId === currentRunId) {
        const firstRun = allRuns.find((run) => run.id !== runId);
        if (firstRun === undefined) {
          await navigate(`/automations/${automationId}`);
        } else {
          await navigate(`/automations/${automationId}/runs/${firstRun.id}`);
        }
      }
      const response = await apiClient.delete(get_run_url(runId));
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AUTOMATION_RUNS_KEY(automationId) });
      await queryClient.invalidateQueries({ queryKey: AUTOMATION_RUN_KEY(automationId, runId) });
      notifications.show({ message: 'Run successfully deleted', color: 'blue' });
    },
  });
};

export default useDeleteRun;
